import { AnalyticsProps, NOT_TRACKED_CONTENT } from 'InternalLibrary';
import { IMaskInput } from 'react-imask';
import classes from 'classnames';
import countryList from 'utils/countryList.json';
import iban from 'iban';
import layout from 'scss/layout.module.scss';
import React, {
  ClipboardEventHandler,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';
import styles from './IBAN.module.scss';
import TextInputEvent from 'hooks/eventCollector/events/TextInputEvent';
import useEventCollector from 'hooks/eventCollector/useEventCollector';

interface Props extends AnalyticsProps {
  countryISO: string;
  onChange?: (iban: string, isValid: boolean) => void;
  onPaste?: (e: ClipboardEventHandler) => void;
  dataTest?: string;
}

export default memo(function IBAN({
  countryISO,
  onChange,
  dataTest,
  analytics,
}: Props) {
  const [value, setValue] = useState('');
  const [valid, setValid] = useState(false);
  const { track } = useEventCollector();
  const pastedRef = useRef(false);

  useEffect(() => {
    if (!value) {
      setValid(true);
    }
  }, [value]);

  const makeMask = () => {
    const { Code, Length } = countryList.filter(
      (elm) => elm.Code.toUpperCase() === countryISO.toUpperCase(),
    )[0];

    const stars = [];
    for (let i = 0; i < Length; i++) {
      stars.push('*');
    }

    return (
      Code.concat(stars.join('').slice(2))
        .match(/.{1,4}/g)
        ?.join(' ') || ''
    );
  };

  const handleChange = (value: string) => {
    const ibanInput = value.toUpperCase() || '';

    setValue(ibanInput);
    const nextValid = iban.isValid(ibanInput);
    setValid(nextValid);
    if (onChange) onChange(ibanInput, nextValid);

    if (analytics?.dontTrack) {
      return;
    }

    if (pastedRef.current && !nextValid) {
      return;
    }

    if (nextValid || value.includes('_')) {
      track(
        new TextInputEvent({
          description: analytics?.description,
          value: analytics?.dontTrackContent
            ? NOT_TRACKED_CONTENT
            : analytics?.contentOverride ??
              ibanInput.replace(/\s/g, '').replaceAll('_', ''),
        }),
      );
    }
  };

  return (
    <div className={styles.iban}>
      <IMaskInput
        mask={makeMask()}
        value={value}
        name="IBAN"
        id="IBAN"
        onAccept={handleChange}
        placeholder=""
        className={classes(
          styles.input,
          layout['full-width-border-box'],
          valid ? styles.success : styles.error,
          valid && !value && styles.natural,
        )}
        data-test={dataTest}
      />
      <label className={styles.label} htmlFor="IBAN">
        Número de cuenta bancaria (IBAN)
      </label>
    </div>
  );
});
